import React from "react";
import { Text, Flex, HelpIndicator } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import {
  sectionContainer,
  sectionHeader,
  accountDetails,
  accountHeader,
  bankIcon,
  detailRow,
  detailLabel,
  detailValue,
  entityLink,
  editEntityLink,
} from "./styles_vanilla.css";
import { AccountDetailsSectionProps } from "./types";
const resolveMappedEntities = (
  bankAccount: AccountDetailsSectionProps["bankAccount"],
  reportingEntityOverride: AccountDetailsSectionProps["reportingEntityOverride"],
): AccountDetailsSectionProps["bankAccount"]["mappedInvestmentEntities"] => {
  if (reportingEntityOverride !== undefined) {
    return reportingEntityOverride ? [reportingEntityOverride] : [];
  }
  return bankAccount.mappedInvestmentEntities ?? [];
};

export const AccountDetailsSection: React.FC<AccountDetailsSectionProps> = ({
  bankAccount,
  onEditEntity,
  getEntityUrl,
  reportingEntityOverride,
}) => {
  // Primary entities from GPx (linked via Account.bank_account_id)
  const gpxEntities = bankAccount.investmentEntities ?? [];
  const hasGpxEntities = gpxEntities.length > 0;

  const mappedEntities = resolveMappedEntities(bankAccount, reportingEntityOverride);
  const hasMappedEntities = mappedEntities.length > 0;

  // Show edit button only when there's NO GPx entity
  const showEditButton = !hasGpxEntities;

  const accountEnding = bankAccount.bankInfoRedacted
    ? bankAccount.bankInfoRedacted.slice(-4)
    : "****";

  return (
    <div className={sectionContainer}>
      <div className={sectionHeader}>ACCOUNT DETAILS</div>

      <div className={accountHeader}>
        <div className={bankIcon}>
          <Icon type="university" size="medium" />
        </div>
        <div className={accountDetails}>
          <Text variant="strong-md" style={{ lineHeight: "1.2" }}>
            {bankAccount.bankName || "Bank"} account ending in -{accountEnding}
          </Text>
          <Text
            variant="regular-md"
            style={{ color: Colors.Neutral600, fontSize: "13px", lineHeight: "1.2" }}
          >
            Account holder{" "}
            <span style={{ fontWeight: 900 }}>{bankAccount.bankAccountName || "—"}</span>
          </Text>
        </div>
      </div>

      <div className={detailRow}>
        <Text className={detailLabel}>Bank account ID</Text>
        <Text className={detailValue}>{bankAccount.bankAccountId}</Text>
      </div>

      {/* Row 1: Payments and reporting entities (from GPx) */}
      <div className={detailRow}>
        <Flex alignItems="flex-start" gap={4}>
          <Flex flexDirection="column">
            <Text className={detailLabel}>Payments and reporting entity</Text>
            <Text className={detailLabel}>(from GPx)</Text>
          </Flex>
          <HelpIndicator
            id="gpx-entity-tooltip"
            placement="top"
            content="Entity configured in GPx for payments and reporting"
          >
            <Icon
              type="question-circle"
              size="small"
              variant="outline"
              style={{ color: Colors.Neutral500, cursor: "pointer" }}
            />
          </HelpIndicator>
        </Flex>
        {hasGpxEntities ? (
          <Flex flexDirection="column" gap={4}>
            {gpxEntities.map((entity) => {
              const url = getEntityUrl?.(entity.entityId);
              return url ? (
                <a
                  key={entity.entityId}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={entityLink}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: Sizes.XS,
                    color: Colors.Primary600,
                    textDecoration: "none",
                  }}
                >
                  {entity.entityName}
                  <Icon type="arrow-up-right-from-square" size="small" />
                </a>
              ) : (
                <Text key={entity.entityId} className={detailValue}>
                  {entity.entityName}
                </Text>
              );
            })}
          </Flex>
        ) : (
          <Text className={detailValue}>—</Text>
        )}
      </div>

      {/* Row 2: Reporting-only entity */}
      <div className={detailRow}>
        <Flex alignItems="center" gap={4}>
          <Text className={detailLabel}>Reporting-only entity</Text>
          <HelpIndicator
            id="reporting-entity-tooltip"
            placement="top"
            content="To enable FundRecs export"
          >
            <Icon
              type="question-circle"
              size="small"
              variant="outline"
              style={{ color: Colors.Neutral500, cursor: "pointer" }}
            />
          </HelpIndicator>
        </Flex>
        <Flex alignItems="center" style={{ flex: 1 }}>
          {hasMappedEntities ? (
            <Flex flexDirection="column" gap={4}>
              {mappedEntities.map((entity) => {
                const url = getEntityUrl?.(entity.entityId);
                return url ? (
                  <a
                    key={entity.entityId}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={entityLink}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: Sizes.XS,
                      color: Colors.Primary600,
                      textDecoration: "none",
                    }}
                  >
                    {entity.entityName}
                    <Icon type="arrow-up-right-from-square" size="small" />
                  </a>
                ) : (
                  <Text key={entity.entityId} className={detailValue}>
                    {entity.entityName}
                  </Text>
                );
              })}
            </Flex>
          ) : (
            <Text className={detailValue}>—</Text>
          )}
          {showEditButton && (
            <button
              type="button"
              className={editEntityLink}
              onClick={onEditEntity}
              data-testid="edit-entity-button"
              style={{ marginLeft: "auto", position: "absolute", right: Sizes.XL }}
            >
              <Icon type="pen-to-square" size="small" />
              Edit entity
            </button>
          )}
        </Flex>
      </div>
    </div>
  );
};

export default AccountDetailsSection;
